/**
 * Offline fallbacks for GET /api/platform/constants → options.
 * Must mirror api-server domain-options/*.options.ts (Title Case labels).
 */
export type DomainOption = {
  value: string;
  label: string;
  description?: string;
};

export type DomainOptionSet = Record<string, readonly DomainOption[]>;

export type DomainOptionsDto = {
  sales_pipeline: DomainOptionSet;
  accounts: DomainOptionSet;
  users: DomainOptionSet;
  design_requests: DomainOptionSet;
  licensing: DomainOptionSet;
  orders: DomainOptionSet;
  payments: DomainOptionSet;
  /** Printer, FC and warehouse flow (schema doc section 5). */
  fulfillment: DomainOptionSet;
  /** Warehouse stock (schema doc section 5). */
  inventory: DomainOptionSet;
};

export type DomainKey = keyof DomainOptionsDto;

export const DEFAULT_DOMAIN_OPTIONS: DomainOptionsDto = {
  sales_pipeline: {
    deal_stage: [
      { value: "reach_out", label: "Reach Outs" },
      { value: "opportunity", label: "Opportunity" },
      { value: "qualified", label: "Qualified" },
      { value: "designing", label: "Designing" },
      { value: "negotiation", label: "Negotiation" },
      { value: "won", label: "Won" },
      { value: "lost", label: "Lost" },
    ],
    reach_out_method: [
      { value: "instagram", label: "Instagram" },
      { value: "text", label: "Text" },
      { value: "linkedin", label: "LinkedIn" },
      { value: "email", label: "Email" },
    ],
    reach_out_status: [
      { value: "initial_reach_out", label: "Initial Reach Out" },
      { value: "in_talks", label: "In Talks" },
      { value: "first_followup", label: "First Followup" },
      { value: "second_followup", label: "Second Followup" },
      { value: "third_followup", label: "Third Followup" },
      { value: "no_response", label: "No Response" },
      { value: "rep_for_other_company", label: "Rep for other company" },
    ],
    deal_mood: [
      { value: "great", label: "Great" },
      { value: "okay", label: "Okay" },
      { value: "danger", label: "Danger" },
      { value: "ghosting", label: "Ghosting" },
    ],
    source_channel: [
      { value: "mql", label: "MQL" },
      { value: "sql", label: "SQL" },
    ],
  },
  accounts: {
    status: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    type: [
      { value: "collegiate", label: "Collegiate" },
      { value: "corporate", label: "Corporate" },
      { value: "other", label: "Other" },
    ],
    client_status: [
      { value: "active", label: "Active" },
      { value: "archived", label: "Archived" },
    ],
  },
  users: {
    activation: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    list_status_filter: [
      { value: "all_active", label: "All Active" },
      { value: "inactive", label: "Inactive" },
      { value: "all", label: "All Users" },
    ],
    team_role: [
      { value: "intern", label: "Intern" },
      { value: "sales", label: "Sales" },
      { value: "designer", label: "Designer" },
      { value: "admin", label: "Admin" },
      { value: "dsa", label: "DSA" },
      { value: "ops", label: "OPS" },
      { value: "licensing_ops", label: "Licensing OPS" },
    ],
    kind: [
      { value: "admin", label: "Admin" },
      { value: "intern", label: "Intern" },
      { value: "client", label: "Client" },
      { value: "habits_team", label: "Habits Team" },
      { value: "other", label: "User" },
      { value: "supplier", label: "Supplier" },
      { value: "printer", label: "Printer" },
      { value: "fc", label: "Fulfillment Center" },
      { value: "warehouse", label: "Warehouse" },
    ],
    /** `users.access_level` 1 to 7; the value is the level as a string. */
    access_level: [
      { value: "1", label: "Admin" },
      { value: "2", label: "Manager" },
      { value: "3", label: "Client" },
      { value: "4", label: "Supplier" },
      { value: "5", label: "Printer" },
      { value: "6", label: "Fulfillment Center" },
      { value: "7", label: "Warehouse" },
    ],
  },
  design_requests: {
    pipeline_status: [
      { value: "submitted", label: "Design Request Submitted" },
      { value: "in_progress", label: "Design In Progress" },
      { value: "design_finished", label: "Design Finished" },
      { value: "revision_requested", label: "Revision Requested" },
      { value: "revision_finished", label: "Revision Finished" },
      { value: "sent_to_client", label: "Sent to Client" },
      { value: "done", label: "Done" },
      { value: "closed", label: "Closed" },
    ],
    version_status: [
      { value: "in_progress", label: "In Progress" },
      { value: "done", label: "Done" },
    ],
    queue_status: [
      { value: "pending", label: "Pending" },
      { value: "claimed", label: "Claimed" },
      { value: "released", label: "Released" },
      { value: "completed", label: "Completed" },
    ],
    stats_bucket: [
      { value: "submitted_pending", label: "Submitted Design Request" },
      { value: "revision_pending", label: "Revision Requested" },
      { value: "in_progress", label: "In Progress" },
      { value: "design_finished", label: "Design Finished" },
      { value: "revision_finished", label: "Revision Finished" },
      { value: "initial_sent", label: "Initial Design" },
      { value: "revision_sent", label: "Revision Sent" },
    ],
    workflow_status: [
      { value: "sent_to_designer", label: "Submitted Design Request" },
      { value: "in_progress", label: "In Progress" },
      { value: "draft_received", label: "Design Finished" },
      { value: "sent_to_client", label: "Sent to Client" },
      { value: "client_requested_revisions", label: "Revision Requested" },
      { value: "revision_received", label: "Revision Finished" },
      { value: "resent_to_client", label: "Revision Sent to Client" },
      { value: "submitted_to_licensing", label: "Sent for Licensing Submission" },
      { value: "licensing_approved", label: "Licensing Approved" },
      { value: "licensing_revision_required", label: "Licensing Revision Required" },
      { value: "final_client_approval", label: "Final Client Approval" },
      { value: "done_closed", label: "Done / Closed" },
      { value: "rejected", label: "Rejected" },
    ],
    quote_request_status: [
      { value: "open", label: "Open" },
      { value: "closed", label: "Closed" },
      { value: "declined", label: "Declined" },
      /** Derived from `reopenedAt`, not stored. Excluded from the status filter. */
      { value: "reopened", label: "Reopened" },
    ],
    quote_order_type: [
      { value: "regular", label: "Bulk Order" },
      { value: "store", label: "Store Order" },
    ],
    designer_status: [
      { value: "new_unclaimed", label: "New / Unclaimed" },
      { value: "in_design", label: "In Design" },
      { value: "draft_completed", label: "Draft Completed" },
      { value: "revision_completed", label: "Revision Completed" },
      { value: "needs_revisions", label: "Needs Revisions" },
    ],
  },
  licensing: {
    status: [
      { value: "pending", label: "Pending" },
      { value: "pre_approved", label: "Pre Approved" },
      { value: "submitted", label: "Awaiting Licensor" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
      { value: "not_needed", label: "Not Needed" },
    ],
    delivery_state: [
      { value: "pending", label: "Sending" },
      { value: "sent", label: "Delivered" },
      { value: "failed", label: "Send Failed" },
    ],
  },
  orders: {
    order_status: [
      { value: "draft", label: "Draft" },
      { value: "needs_approval", label: "Needs Approval" },
      { value: "live_store", label: "Live Store" },
      { value: "moq_review", label: "MOQ Review" },
      { value: "on_hold", label: "On Hold" },
      { value: "approved_for_payment", label: "Approved for Payment" },
      { value: "needs_assignment", label: "Needs Assignment" },
      { value: "in_production", label: "In Production" },
      { value: "shipping", label: "Shipping" },
      { value: "shipped", label: "Shipped" },
      { value: "delivered", label: "Delivered" },
      { value: "failed_store", label: "Failed Store" },
      { value: "cancelled", label: "Cancelled" },
    ],
    order_item_status: [
      { value: "pending_pricing_review", label: "Pending Pricing Review" },
      { value: "live_store_item", label: "Live Store Item" },
      { value: "moq_review", label: "MOQ Review" },
      { value: "failed_store_item", label: "Failed Store Item" },
      { value: "approved_for_payment", label: "Approved for Payment" },
      { value: "needs_assignment", label: "Needs Assignment" },
      { value: "supplier_assigned", label: "Supplier Assigned" },
      { value: "in_sampling", label: "In Sampling" },
      { value: "manufacturing", label: "Manufacturing" },
      { value: "printer_assigned", label: "Printer Assigned" },
      { value: "in_transit_to_printer", label: "In Transit to Printer" },
      { value: "delivered_to_printer", label: "Delivered to Printer" },
      { value: "printing_in_progress", label: "Printing in Progress" },
      { value: "in_transit_to_fc", label: "In Transit to FC" },
      { value: "delivered_to_fc", label: "Delivered to FC" },
      { value: "fc_in_progress", label: "FC in Progress" },
      { value: "shipping", label: "Shipping" },
      { value: "shipped", label: "Shipped" },
      { value: "delivered", label: "Delivered" },
      { value: "cancelled", label: "Cancelled" },
    ],
    order_type: [
      { value: "regular", label: "Bulk" },
      { value: "store", label: "Store" },
    ],
    shipping_type: [
      { value: "bulk", label: "Bulk" },
      { value: "individual", label: "Individual" },
    ],
    store_type: [
      { value: "store", label: "Group Store" },
      { value: "open_store", label: "Open Store" },
      { value: "fundraiser", label: "Fundraiser" },
      { value: "brand_drop", label: "Brand Drop" },
    ],
    payment_status: [
      { value: "pending", label: "Pending" },
      { value: "processing", label: "Processing" },
      { value: "paid", label: "Paid" },
      { value: "partially_paid", label: "Partially Paid" },
      { value: "failed", label: "Failed" },
      { value: "refunded", label: "Refunded" },
      { value: "partially_refunded", label: "Partially Refunded" },
    ],
    review_type: [
      { value: "pricing", label: "Pricing" },
      { value: "stock", label: "Stock" },
      { value: "design", label: "Design" },
      { value: "licensing", label: "Licensing" },
    ],
    order_review_type: [{ value: "shipping", label: "Shipping" }],
    review_status: [
      { value: "pending", label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "not_approved", label: "Not Approved" },
      { value: "not_required", label: "Not Required" },
    ],
    pricing_review_result: [
      { value: "within_margin", label: "Within Margin" },
      { value: "below_margin", label: "Below Margin" },
      { value: "override_approved", label: "Override Approved" },
    ],
    moq_review_status: [
      { value: "pending", label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "not_approved", label: "Not Approved" },
    ],
    moq_review_result: [
      { value: "met", label: "Met" },
      { value: "adjusted", label: "Adjusted" },
      { value: "not_met", label: "Not Met" },
    ],
    participant_kind: [
      { value: "store_buyer", label: "Store Buyer" },
      { value: "bulk_roster", label: "Bulk Roster" },
    ],
    link_pricing_mode: [
      { value: "price_stays_same", label: "Price Stays Same" },
      { value: "price_drops", label: "Price Drops" },
    ],
    payment_processor: [
      { value: "authorize_net", label: "Authorize.Net" },
      { value: "braintree_paypal", label: "Braintree PayPal" },
      { value: "manual_entry", label: "Manual Entry" },
    ],
    payment_method: [
      { value: "0", label: "Other" },
      { value: "1", label: "Card" },
      { value: "2", label: "Google Pay" },
      { value: "3", label: "PayPal" },
      { value: "4", label: "Venmo" },
      { value: "5", label: "Apple Pay" },
      { value: "9", label: "Zelle" },
      { value: "6", label: "Cash/Check" },
      { value: "7", label: "Wire Transfer" },
      { value: "10", label: "Bank Transfer" },
      { value: "8", label: "Refund" },
    ],
  },
  /** Mirrors api-server domain-options/payments.options.ts. */
  payments: {
    lane: [
      { value: "pending", label: "Payment Pending" },
      { value: "link_open", label: "Link Open" },
      { value: "link_closed", label: "Link Closed" },
      { value: "invoice_sent", label: "Invoice Sent" },
      { value: "cheque_sent", label: "Cheque Sent" },
      { value: "partially_paid", label: "Partially Paid" },
      { value: "paid", label: "Paid" },
      { value: "refunded", label: "Refunded" },
      { value: "dropped", label: "Dropped" },
    ],
    settlement: [
      { value: "promised", label: "In the Mail" },
      { value: "received", label: "In Hand" },
      { value: "cleared", label: "Cleared" },
      { value: "bounced", label: "Bounced" },
    ],
    method: [
      { value: "credit_card", label: "Credit Card" },
      { value: "bank_transfer", label: "Bank Transfer" },
      { value: "venmo", label: "Venmo" },
      { value: "check", label: "Check" },
      { value: "cash", label: "Cash" },
      { value: "other", label: "Other" },
    ],
    record_method: [
      { value: "0", label: "Other", description: "other" },
      { value: "1", label: "Card", description: "credit_card" },
      { value: "2", label: "Google Pay", description: "credit_card" },
      { value: "3", label: "PayPal", description: "credit_card" },
      { value: "4", label: "Venmo", description: "venmo" },
      { value: "5", label: "Apple Pay", description: "credit_card" },
      { value: "9", label: "Zelle", description: "other" },
      { value: "6", label: "Cash/Check", description: "check" },
      { value: "7", label: "Wire Transfer", description: "bank_transfer" },
      { value: "10", label: "Bank Transfer", description: "bank_transfer" },
    ],
    production_status: [
      { value: "not_released", label: "Not in Production" },
      { value: "released_paid", label: "In Production" },
      { value: "released_part_paid", label: "In Production, Part Paid" },
      { value: "released_unpaid", label: "In Production, Unpaid" },
      { value: "recalled", label: "Recalled from Production" },
    ],
    refund_channel: [
      { value: "paypal", label: "PayPal" },
      { value: "manual", label: "Manual" },
    ],
    refund_request_status: [
      { value: "pending", label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
    ],
    at_release: [
      { value: "in_full", label: "Paid in Full" },
      { value: "part", label: "Partly Paid" },
      { value: "none", label: "Unpaid" },
    ],
    order_type: [
      { value: "bulk", label: "Bulk" },
      { value: "link", label: "Link" },
      { value: "store", label: "Store" },
    ],
  },
  /**
   * Mirrors api-server domain-options/fulfillment.options.ts. Coded groups
   * carry their code only in the API; the client renders by value. Hint copy
   * that is not a status name lives in `description` (schema doc 9.8).
   */
  fulfillment: {
    printer_order_stage: [
      { value: "blanks_in_transit", label: "Blanks in Transit" },
      { value: "blanks_received", label: "Blanks Received" },
      { value: "in_production", label: "In Production" },
      { value: "shipping", label: "Shipping" },
      { value: "internal_transit", label: "Internal Transit" },
      { value: "shipped", label: "Shipped" },
      { value: "delivered", label: "Delivered" },
      { value: "cancelled", label: "Cancelled" },
    ],
    /** Staff Printer Orders board (prototype PrinterOrdersBoard): lane order and hints. */
    printer_board_lane: [
      { value: "blanks_in_transit", label: "Blanks in Transit", description: "Printer Assigned, blanks on the way" },
      { value: "blanks_received", label: "Blanks Received", description: "Blanks at the printer, ready to print" },
      { value: "in_production", label: "In Production", description: "In production at the current stop" },
      { value: "internal_transit", label: "Internal Transit", description: "On the road to the next printer or FC" },
      { value: "shipping", label: "Shipping", description: "Last stop, boxes going out" },
      { value: "shipped", label: "Shipped", description: "Has tracking, every box labelled" },
      { value: "delivered", label: "Delivered", description: "Tracking shows delivered, or the team marks it" },
      { value: "cancelled", label: "Cancelled", description: "Unassigned parts, kept for reference" },
    ],
    /** Printer portal board (prototype PortalBoard, F1). */
    printer_portal_lane: [
      { value: "blanks_in_transit", label: "Blanks in Transit", description: "Blanks on the way to you" },
      { value: "blanks_received", label: "Blanks Received", description: "Ready to print" },
      { value: "in_production", label: "In Production", description: "In production now" },
      { value: "shipping", label: "Shipping", description: "Labels created, getting ready to ship" },
      { value: "shipped", label: "Shipped", description: "Has tracking, every box labelled" },
      { value: "delivered", label: "Delivered", description: "Tracking shows delivered, or the team marks it" },
      { value: "cancelled", label: "Cancelled", description: "Cancelled by Habits, no work needed" },
    ],
    order_destination: [
      { value: "client", label: "Client" },
      { value: "participants", label: "Participants" },
      { value: "fc", label: "Fulfillment Center" },
    ],
    stop_kind: [
      { value: "printer", label: "Printer" },
      { value: "fc", label: "Fulfillment Center" },
    ],
    fc_order_stage: [
      { value: "packages_in_transit", label: "Packages In Transit" },
      { value: "packages_received", label: "Packages Received" },
      { value: "in_production", label: "In Production", description: "FC in Progress" },
      { value: "shipping", label: "Shipping" },
      { value: "shipped", label: "Shipped" },
      { value: "delivered", label: "Delivered" },
      { value: "cancelled", label: "Cancelled" },
    ],
    /** Staff FC Orders board (prototype FCBoard). */
    fc_board_lane: [
      { value: "packages_in_transit", label: "Packages In Transit", description: "Packages on the way to the FC" },
      { value: "packages_received", label: "Packages Received", description: "Received at the FC" },
      { value: "in_production", label: "In Production", description: "FC in Progress: sorting and packing" },
      { value: "shipping", label: "Shipping", description: "Labels or tracking added, ready for the carrier" },
      { value: "shipped", label: "Shipped", description: "Has tracking and every started label is made" },
      { value: "delivered", label: "Delivered" },
      { value: "cancelled", label: "Cancelled", description: "Cancelled by Habits" },
    ],
    /** FC portal board (prototype FCPortalBoard, K1). */
    fc_portal_lane: [
      { value: "packages_in_transit", label: "Packages In Transit", description: "Packages on the way to you" },
      { value: "packages_received", label: "Packages Received", description: "Arrived, ready to pack" },
      { value: "in_production", label: "In Production", description: "Sorting and packing by participant" },
      { value: "shipping", label: "Shipping", description: "Labels or tracking added, ready for the carrier" },
      { value: "shipped", label: "Shipped", description: "Has tracking and every started label is made" },
      { value: "delivered", label: "Delivered" },
      { value: "cancelled", label: "Cancelled", description: "Cancelled by Habits, no work needed" },
    ],
    warehouse_order_stage: [
      { value: "new", label: "New" },
      { value: "packing", label: "Packing" },
      { value: "shipping", label: "Shipping" },
      { value: "shipped", label: "Shipped" },
      { value: "cancelled", label: "Cancelled" },
    ],
    /** Staff Warehouse Orders board (prototype WarehouseBoard). */
    warehouse_board_lane: [
      { value: "new", label: "New", description: "Assigned, not started" },
      { value: "packing", label: "Packing", description: "Picking and boxing for the printer" },
      { value: "shipping", label: "Shipping", description: "Boxed, going to the printer" },
      { value: "shipped", label: "Shipped", description: "On the way to the printer" },
      { value: "cancelled", label: "Cancelled", description: "Cancelled by Habits, hold released" },
    ],
    /** Warehouse portal board (prototype WarehousePortalBoard). */
    warehouse_portal_lane: [
      { value: "new", label: "New", description: "Assigned, not started" },
      { value: "packing", label: "Packing", description: "Picking and boxing for the printer" },
      { value: "shipping", label: "Shipping", description: "Boxed, going to the printer" },
      { value: "shipped", label: "Shipped", description: "On the way to the printer" },
      { value: "cancelled", label: "Cancelled", description: "Cancelled by Habits, no work needed" },
    ],
    /** Hints: prototype BlanksBoard2 lanes. */
    blanks_order_status: [
      { value: "ordered", label: "Ordered", description: "Waiting for the distributor or warehouse to ship" },
      { value: "shipped", label: "Shipped", description: "On the way to the printer" },
      { value: "part_received", label: "Part Received", description: "Some items confirmed by the printer" },
      { value: "received", label: "Received", description: "The printer confirms every item" },
      { value: "cancelled", label: "Cancelled", description: "Part unassigned or order cancelled" },
    ],
    blanks_source: [
      { value: "distributor", label: "Distributor" },
      { value: "warehouse", label: "Warehouse" },
    ],
    distributor_ordering_mode: [
      { value: "manual", label: "Manual" },
      { value: "integrated", label: "Integrated" },
    ],
    issue_type: [
      { value: "art", label: "Art" },
      { value: "blanks_count", label: "Blanks Count" },
      { value: "blanks_damaged", label: "Blanks Damaged" },
      { value: "wrong_item", label: "Wrong Item" },
      { value: "other", label: "Other" },
      { value: "general", label: "General" },
    ],
    issue_status: [
      { value: "open", label: "Open" },
      { value: "resolved", label: "Resolved" },
    ],
    package_source: [
      { value: "manual", label: "Manual" },
      { value: "label", label: "Label" },
    ],
    label_state: [
      { value: "none", label: "None" },
      { value: "not_created", label: "Not Created" },
      { value: "purchasing", label: "Purchasing" },
      { value: "label_ready", label: "Label Ready" },
      { value: "scanned", label: "Scanned" },
      { value: "failed", label: "Failed" },
    ],
    shipment_kind: [
      { value: "bulk", label: "Bulk" },
      { value: "individual", label: "Individual" },
    ],
    service_level: [
      { value: "ground", label: "Ground" },
      { value: "two_day", label: "Two Day" },
      { value: "overnight", label: "Overnight" },
    ],
    partner_actor_org: [
      { value: "habits", label: "Habits" },
      { value: "printer", label: "Printer" },
      { value: "fc", label: "Fulfillment Center" },
      { value: "warehouse", label: "Warehouse" },
      { value: "system", label: "System" },
    ],
    history_category: [
      { value: "production", label: "Production" },
      { value: "blanks", label: "Blanks" },
      { value: "labels", label: "Labels" },
      { value: "issues", label: "Issues" },
      { value: "notes", label: "Notes" },
    ],
    partner_kind: [
      { value: "printer", label: "Printer" },
      { value: "fc", label: "Fulfillment Center" },
      { value: "warehouse", label: "Warehouse" },
      { value: "distributor", label: "Distributor" },
    ],
    partner_setup_change_type: [
      { value: "created", label: "Created" },
      { value: "renamed", label: "Renamed" },
      { value: "address_changed", label: "Address Changed" },
      { value: "deactivated", label: "Deactivated" },
      { value: "reactivated", label: "Reactivated" },
      { value: "login_linked", label: "Login Linked" },
      { value: "login_unlinked", label: "Login Unlinked" },
      { value: "capacity_changed", label: "Capacity Changed" },
      { value: "setup_changed", label: "Setup Changed" },
      { value: "distributor_mode_changed", label: "Ordering Mode Changed" },
    ],
  },
  /** Mirrors api-server domain-options/inventory.options.ts. */
  inventory: {
    stock_edit_reason: [
      { value: "count", label: "Count" },
      { value: "damage", label: "Damage" },
      { value: "correction", label: "Correction" },
    ],
    stock_log_kind: [
      { value: "hold", label: "Hold" },
      { value: "release", label: "Release" },
      { value: "ship_out", label: "Ship Out" },
      { value: "edit", label: "Edit" },
      { value: "restock_planned", label: "Restock Planned" },
      { value: "restock_edited", label: "Restock Edited" },
      { value: "restock_received", label: "Restock Received" },
      { value: "restock_cancelled", label: "Restock Cancelled" },
      { value: "restock_closed", label: "Restock Closed" },
    ],
    restock_status: [
      { value: "planned", label: "Planned" },
      { value: "part_received", label: "Part Received" },
      { value: "received", label: "Received" },
      { value: "closed", label: "Closed" },
      { value: "cancelled", label: "Cancelled" },
    ],
    stock_level: [
      { value: "out", label: "Out of Stock" },
      { value: "below_25", label: "Below 25" },
      { value: "below_50", label: "Below 50" },
      { value: "ok", label: "50 and Up" },
    ],
    /** Invoices list cuts — see modules/Payments/invoices. */
    invoice_status_group: [
      { value: "all", label: "All" },
      { value: "needs_attention", label: "Needs Attention" },
      { value: "unpaid_overdue", label: "Overdue" },
      { value: "unpaid_not_due", label: "Not Due" },
      { value: "paid", label: "Paid" },
      { value: "cancelled", label: "Cancelled" },
    ],
    /** Why an invoice needs a human — severity order lives in the module. */
    attention_reason: [
      { value: "cancelled_with_balance", label: "Cancelled With Balance" },
      { value: "delivery_issue", label: "Delivery Issue" },
      { value: "overdue", label: "Overdue" },
      { value: "follow_up_due", label: "Follow-up Due" },
      { value: "never_emailed", label: "Never Emailed" },
      { value: "sync_stale", label: "Not Synced" },
    ],
  },
};

export type DesignRequestPipelineStatus =
  (typeof DEFAULT_DOMAIN_OPTIONS.design_requests.pipeline_status)[number]["value"];

export type DesignRequestWorkflowStatus =
  (typeof DEFAULT_DOMAIN_OPTIONS.design_requests.workflow_status)[number]["value"];

export type DealStageId =
  (typeof DEFAULT_DOMAIN_OPTIONS.sales_pipeline.deal_stage)[number]["value"];

export type DealMoodKey =
  (typeof DEFAULT_DOMAIN_OPTIONS.sales_pipeline.deal_mood)[number]["value"];

export type DealSourceChannel =
  (typeof DEFAULT_DOMAIN_OPTIONS.sales_pipeline.source_channel)[number]["value"];

export type AccountStatus =
  (typeof DEFAULT_DOMAIN_OPTIONS.accounts.status)[number]["value"];

export type AccountType =
  (typeof DEFAULT_DOMAIN_OPTIONS.accounts.type)[number]["value"];

export type UserListStatusFilter =
  (typeof DEFAULT_DOMAIN_OPTIONS.users.list_status_filter)[number]["value"];

export type UserTeamRole =
  (typeof DEFAULT_DOMAIN_OPTIONS.users.team_role)[number]["value"];

export type UserKindOption =
  (typeof DEFAULT_DOMAIN_OPTIONS.users.kind)[number]["value"];

export type DesignRequestDesignerStatus =
  (typeof DEFAULT_DOMAIN_OPTIONS.design_requests.designer_status)[number]["value"];
